/*
 * ClientAutofill — the Proposal Mailer's "pick a client I already have on file"
 * controls. Two exports are used by ProposalMailerPage:
 *
 *   <ClientPicker onPick={...} />        above Client Name, on the Compose panel
 *   <BulkClientAdder onAdd={...} />      above the Bulk Send recipients box
 *
 * Deliberately dependency-free — it imports React and nothing else. Every other
 * file on this screen pulls shadcn primitives through the app's own alias, and a
 * wrong alias is a build failure rather than a visible mistake. So the two
 * controls are plain elements carrying the *same* Tailwind class strings the
 * shadcn Input and Label already render on this page. They match the screen
 * because they are wearing its clothes, not because they imported them.
 *
 * The client list comes from GET /api/clients?limit=200. That endpoint is
 * readable by every logged-in user (all recruiters can see all clients), so this
 * works for Smita and Vaibhavi exactly as it does for Ruhshad. The default page
 * size is 50, hence the explicit limit.
 *
 * Two facts about the stored data shape this file:
 *
 *  1. Each client row carries the same person twice — contact_name/contact_email/
 *     contact_phone AND contact_person/email/phone. Measured across all 22 live
 *     records they never disagree, so there is no second contact to offer and no
 *     tick-list to build. `pickFirst` simply takes whichever of the pair is
 *     populated, which is why coverage here beats either field alone: on live,
 *     contact_email is filled for 21 of 22 and email for 22 of 22.
 *  2. Four clients were never imported from JM and carry no address and no
 *     GSTIN. Every context row is therefore rendered only when it has a value —
 *     a blank labelled row would look like data loss.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/* The exact class strings the shadcn <Input> and <Label> render on this screen. */
const INPUT_CLS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base ' +
  'shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none ' +
  'focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed ' +
  'disabled:opacity-50 md:text-sm';
const LABEL_CLS = 'text-sm font-medium leading-none';

/* ------------------------------------------------------------------ helpers */

/** First of the candidates that actually holds something. Trimmed. */
export function pickFirst(...values) {
  for (const v of values) {
    if (v !== null && v !== undefined && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/**
 * Flatten one stored client into the handful of fields this screen cares about.
 * Kept exported and pure so it can be checked against real records without a
 * browser.
 */
export function normaliseClient(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: pickFirst(raw.name),
    contact: pickFirst(raw.contact_name, raw.contact_person),
    email: pickFirst(raw.contact_email, raw.email),
    phone: pickFirst(raw.contact_phone, raw.phone),
    address: pickFirst(raw.address),
    gstin: pickFirst(raw.gstin),
    city: pickFirst(raw.city),
    state: pickFirst(raw.state),
  };
}

/** Loose search across the fields someone would actually type. */
export function clientMatches(c, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return q
    .split(/\s+/)
    .every((term) =>
      [c.name, c.contact, c.email, c.phone, c.city, c.state]
        .join(' ')
        .toLowerCase()
        .includes(term));
}

/** The Bulk Send box's own format, taken from its placeholder: "Name, email". */
export function toRecipientLine(c) {
  const who = pickFirst(c.name, c.contact);
  if (!c.email || !who) return null;
  return who + ', ' + c.email;
}

/**
 * Append picked clients to whatever is already typed in the recipients box.
 * Never replaces, never reorders, and never adds an address that is already
 * somewhere in the box — re-picking the same client twice is a no-op rather
 * than a double send.
 */
export function appendRecipients(existing, clients) {
  const current = String(existing || '');
  const seen = new Set(
    current
      .split('\n')
      .map((l) => {
        const m = l.match(/[^\s,;<>]+@[^\s,;<>]+/);
        return m ? m[0].toLowerCase() : '';
      })
      .filter(Boolean));

  const added = [];
  const skippedNoEmail = [];
  const skippedDuplicate = [];

  clients.forEach((c) => {
    const line = toRecipientLine(c);
    if (!line) {
      skippedNoEmail.push(pickFirst(c.name, c.contact, '(unnamed)'));
      return;
    }
    const key = c.email.toLowerCase();
    if (seen.has(key)) {
      skippedDuplicate.push(pickFirst(c.name, c.contact));
      return;
    }
    seen.add(key);
    added.push(line);
  });

  if (!added.length) return { text: current, added, skippedNoEmail, skippedDuplicate };
  const head = current.replace(/\s+$/, '');
  const text = (head ? head + '\n' : '') + added.join('\n') + '\n';
  return { text, added, skippedNoEmail, skippedDuplicate };
}

/* -------------------------------------------------------------------- data */

/** Fetches the client list once and shares it between both controls. */
export function useClients() {
  const [clients, setClients] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState('');
  const started = useRef(false);

  const load = useCallback(async () => {
    setState('loading');
    setError('');
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(API_BASE + '/clients?limit=200', {
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.clients || data.items || []);
      const list = rows.map(normaliseClient).filter(Boolean);
      list.sort((a, b) => a.name.localeCompare(b.name));
      setClients(list);
      setState('ready');
    } catch (e) {
      setError(String((e && e.message) || e));
      setState('error');
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  return { clients, state, error, reload: load };
}

/* ----------------------------------------------------------- small pieces */

function ContextRow({ label, value }) {
  if (!value) return null; // never render a blank labelled row
  return (
    <div className="flex gap-2 text-xs leading-relaxed">
      <span className="w-16 flex-shrink-0 text-slate-400">{label}</span>
      <span className="text-slate-600 break-words">{value}</span>
    </div>
  );
}

function ResultList({ items, onChoose, emptyText }) {
  if (!items.length) {
    return <div className="px-3 py-2 text-xs text-slate-400">{emptyText}</div>;
  }
  return (
    <ul className="max-h-56 overflow-y-auto">
      {items.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => onChoose(c)}
            className="w-full px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
            data-testid={'client-pick-' + c.id}
          >
            <div className="text-sm text-slate-800">{c.name || c.contact}</div>
            <div className="text-xs text-slate-500">
              {c.email || 'no email on file'}
              {c.contact && c.contact !== c.name ? ' · ' + c.contact : ''}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------ the picker */

/**
 * onPick receives the normalised client. The page decides what to fill, so this
 * component never reaches into the form — the boxes stay ordinary editable
 * inputs and typing a client who is not on file still works exactly as before.
 */
export function ClientPicker({ onPick }) {
  const { clients, state, error, reload } = useClients();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    function away(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const shown = useMemo(
    () => clients.filter((c) => clientMatches(c, query)).slice(0, 40),
    [clients, query]);

  const choose = (c) => {
    setChosen(c);
    setQuery('');
    setOpen(false);
    if (onPick) onPick(c);
  };

  const hasContext = chosen && (chosen.phone || chosen.address || chosen.gstin ||
    chosen.city || chosen.state || chosen.contact);

  return (
    <div className="space-y-2" data-testid="client-picker" ref={boxRef}>
      <div className="flex items-center justify-between">
        <label className={LABEL_CLS}>Pick a saved client</label>
        {state === 'ready' && (
          <span className="text-xs text-slate-400">{clients.length} on file</span>
        )}
      </div>

      <div className="relative">
        <input
          className={INPUT_CLS}
          placeholder={state === 'loading' ? 'Loading your clients…' : 'Search your clients…'}
          value={query}
          disabled={state === 'loading'}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid="client-picker-search"
        />
        {open && state === 'ready' && (
          <div className="absolute z-30 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
            <ResultList
              items={shown}
              onChoose={choose}
              emptyText={'No client matches that. You can still type the details by hand below.'}
            />
          </div>
        )}
      </div>

      {state === 'error' && (
        <div className="text-xs text-amber-700">
          Could not load your client list ({error}). Type the details by hand below, or{' '}
          <button type="button" className="underline" onClick={reload}>try again</button>.
        </div>
      )}

      {chosen && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="client-picker-context">
          <div className="mb-1 flex items-start justify-between gap-2">
            <div className="text-sm font-medium text-slate-800">{chosen.name || chosen.contact}</div>
            <button
              type="button"
              onClick={() => { setChosen(null); }}
              className="text-xs text-slate-500 underline hover:text-slate-700"
              data-testid="client-picker-clear"
            >
              clear
            </button>
          </div>
          {hasContext ? (
            <div className="space-y-0.5">
              <ContextRow label="Contact" value={chosen.contact} />
              <ContextRow label="Phone" value={chosen.phone} />
              <ContextRow label="Address" value={chosen.address} />
              <ContextRow label="GSTIN" value={chosen.gstin} />
              <ContextRow
                label="City"
                value={[chosen.city, chosen.state].filter(Boolean).join(', ')}
              />
            </div>
          ) : (
            <div className="text-xs text-slate-400">
              Nothing else on file for this client yet — the mail below asks them for it.
            </div>
          )}
          {!chosen.email && (
            <div className="mt-1 text-xs text-amber-700">
              No email stored for this client — type one into Client Email below.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------- bulk multi-select adder */

/**
 * onAdd receives the finished text for the recipients box. The caller passes in
 * whatever is currently typed there, so nothing is ever thrown away.
 */
export function BulkClientAdder({ currentValue, onAdd }) {
  const { clients, state, error, reload } = useClients();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [ticked, setTicked] = useState({});
  const [note, setNote] = useState('');

  const shown = useMemo(
    () => clients.filter((c) => clientMatches(c, query)),
    [clients, query]);

  const chosen = useMemo(
    () => clients.filter((c) => ticked[c.id]),
    [clients, ticked]);

  const toggle = (id) => setTicked((t) => Object.assign({}, t, { [id]: !t[id] }));

  const add = () => {
    const result = appendRecipients(currentValue, chosen);
    if (onAdd) onAdd(result.text);
    const bits = [];
    if (result.added.length) bits.push('Added ' + result.added.length);
    if (result.skippedDuplicate.length) bits.push(result.skippedDuplicate.length + ' already there');
    if (result.skippedNoEmail.length) bits.push(result.skippedNoEmail.length + ' with no email on file');
    setNote(bits.join(' · ') || 'Nothing to add');
    setTicked({});
    setOpen(false);
  };

  return (
    <div className="space-y-2" data-testid="bulk-client-adder">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-8 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input px-3 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          data-testid="bulk-add-from-clients-btn"
        >
          {open ? 'Close client list' : 'Add from my clients'}
        </button>
        {state === 'ready' && !open && (
          <span className="text-xs text-slate-400">{clients.length} on file</span>
        )}
        {note && <span className="text-xs text-slate-500" data-testid="bulk-add-note">{note}</span>}
      </div>

      {state === 'error' && (
        <div className="text-xs text-amber-700">
          Could not load your client list ({error}).{' '}
          <button type="button" className="underline" onClick={reload}>Try again</button>.
        </div>
      )}

      {open && (
        <div className="rounded-md border border-slate-200 bg-white p-2 shadow-sm">
          <input
            className={INPUT_CLS}
            placeholder="Search your clients…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="bulk-client-search"
          />
          <ul className="mt-2 max-h-60 space-y-0.5 overflow-y-auto">
            {shown.map((c) => (
              <li key={c.id}>
                <label className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 flex-shrink-0"
                    checked={!!ticked[c.id]}
                    onChange={() => toggle(c.id)}
                    data-testid={'bulk-client-tick-' + c.id}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm text-slate-800">{c.name || c.contact}</span>
                    <span className="block text-xs text-slate-500">
                      {c.email || 'no email on file — will be skipped'}
                    </span>
                  </span>
                </label>
              </li>
            ))}
            {!shown.length && (
              <li className="px-2 py-2 text-xs text-slate-400">No client matches that.</li>
            )}
          </ul>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={add}
              disabled={!chosen.length}
              className="inline-flex h-8 items-center justify-center rounded-md bg-slate-900 px-3 text-xs font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-40"
              data-testid="bulk-add-selected-btn"
            >
              Add {chosen.length || ''} to the list
            </button>
            <span className="text-xs text-slate-400">Adds to what is already typed — never replaces it.</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientPicker;
